import React, { useMemo } from 'react';
import { useTenant } from '../../context/TenantContext.tsx';
import { ProjectMultiSelect } from './ProjectMultiSelect.tsx';
import type { Task } from '../../../shared/types.ts';

// Team filter that sits next to the project filter. A team is its members, so picking one or more
// teams shows the tasks assigned to those teams' members, whichever board they are on.

/** The ids of everyone in the picked teams, or null when no team is picked (= everyone) */
export function useTeamMemberIds(selectedTeamIds: string[]): Set<string> | null {
  const { spaces } = useTenant();
  return useMemo(() => {
    if (selectedTeamIds.length === 0) return null;
    const ids = new Set<string>();
    for (const s of spaces || []) {
      if (!selectedTeamIds.includes(s.id)) continue;
      for (const m of s.members || []) if (m?.id) ids.add(m.id);
    }
    return ids;
  }, [spaces, selectedTeamIds]);
}

/** Does this task belong to someone in the picked teams? */
export function taskInTeams(task: Task, memberIds: Set<string> | null): boolean {
  if (!memberIds) return true;
  if (task.assigneeId && memberIds.has(task.assigneeId)) return true;
  return !!(task as any).assignees?.some((a: { id: string }) => memberIds.has(a.id));
}

interface TeamSelectProps {
  selectedTeamIds: string[];
  onChange: (teamIds: string[]) => void;
  className?: string;
}

export const TeamSelect: React.FC<TeamSelectProps> = ({ selectedTeamIds, onChange, className }) => {
  const { spaces } = useTenant();

  const options = useMemo(
    () =>
      (spaces || []).map((s) => ({
        id: s.id,
        name: s.name,
        key: `${s.members?.length || 0} members`,
      })),
    [spaces]
  );

  if (options.length === 0) return null;

  return (
    <ProjectMultiSelect
      className={className}
      projects={options}
      selectedIds={selectedTeamIds}
      allLabel="All Teams"
      // fixed width so picking a team never pushes the other filters onto a new line
      buttonWidthClass="w-[118px] justify-between"
      noun="team"
      searchPlaceholder="Search teams..."
      onChange={onChange}
    />
  );
};
